import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axios/axios';
import {
  offerwallApi,
  useGetAdsQuery,
} from '../../../services/gmoOfferwallApi';
import { Layout } from '../../components/Layout';
import {
  createOfferwallCsvContent,
  OFFERWALL_CSV_HEADER_LABELS,
  OFFERWALL_CSV_HEADERS,
} from '../OfferwallPage/form';

type OfferwallImportResponse = {
  inserted: number;
  skipped: number;
};

export function OfferwallCsvPage() {
  const dispatch = useDispatch();
  const { data = [], isLoading } = useGetAdsQuery();
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const exampleHeaders = useMemo(
    () =>
      OFFERWALL_CSV_HEADERS.map(
        header => OFFERWALL_CSV_HEADER_LABELS[header],
      ).join(', '),
    [],
  );

  const handleExport = () => {
    const csvContent = createOfferwallCsvContent(data);
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `offerwall_export_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('CSVファイルを選択してください。', {
        position: 'bottom-left',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);

    try {
      setIsImporting(true);
      const response = await axiosInstance.post<OfferwallImportResponse>(
        '/offerwall_import_csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      toast.success(
        `${response.inserted}件を取り込みました。空行スキップ: ${response.skipped}件`,
        {
          position: 'bottom-left',
        },
      );
      dispatch(offerwallApi.util.invalidateTags(['offerwall']));
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(
        error?.message || 'CSVの取り込みに失敗しました。ファイル内容を確認してください。',
        {
          position: 'bottom-left',
          autoClose: 8000,
        },
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Layout title={'案件CSV管理'}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5">案件CSV管理</Typography>
            <Typography variant="body2" color="text.secondary">
              エクスポートしたCSVをそのまま編集して、複数行まとめて取り込めます。
            </Typography>
            <Alert severity="info">
              CSVヘッダは固定です: {exampleHeaders}
            </Alert>
            <Box>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={isLoading || isImporting}
              >
                現在の案件をCSV出力
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6">CSVインポート</Typography>
            <Typography variant="body2" color="text.secondary">
              1行につき1件追加されます。既存データの更新ではなく、新規追加です。
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={event => {
                const selectedFile = event.target.files?.[0] || null;
                setFile(selectedFile);
              }}
            />
            <Typography variant="body2">
              選択中: {file?.name || '未選択'}
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleImport}
                disabled={!file || isImporting}
                startIcon={isImporting ? <CircularProgress size={18} /> : null}
              >
                {isImporting ? '取り込み中...' : 'CSVを取り込む'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Layout>
  );
}

export default OfferwallCsvPage;
